import { useMemo, useState } from "react";
import { App, Input, Table, Tooltip } from "antd";
import {
  CreditCardOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import {
  isSystemPaymentMethod,
  type PaymentMethod,
  type PaymentMethodCreatePayload,
  type PaymentMethodUpdatePayload,
} from "../../../../api/paymentMethods";
import {
  useCreatePaymentMethod,
  useDeletePaymentMethod,
  useUpdatePaymentMethod,
} from "../../../../hooks/usePaymentMethods";
import DeleteConfirmModal from "../DeleteConfirmModal";
import PaymentMethodModal from "./PaymentMethodModal";
import {
  ConfigPanel,
  PanelAddButton,
  PanelIconAction,
} from "../panel/ConfigPanel";
import { PANEL_ACCENTS } from "../panel/panelAccents";

const R = {
  bg: "var(--color-canvas)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
};

// Една секция на страницата → основният оранжев акцент.
const accent = PANEL_ACCENTS.amber;

interface Props {
  paymentMethods: PaymentMethod[];
  isNarrow?: boolean;
}

const PaymentMethodsTable = ({ paymentMethods, isNarrow = false }: Props) => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();

  const createMutation = useCreatePaymentMethod();
  const updateMutation = useUpdatePaymentMethod();
  const deleteMutation = useDeletePaymentMethod();

  const [searchText, setSearchText] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PaymentMethod | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PaymentMethod | null>(null);

  const filtered = useMemo(() => {
    if (!searchText.trim()) return paymentMethods;
    const q = searchText.toLowerCase();
    return paymentMethods.filter(
      (pm) =>
        pm.name_bg.toLowerCase().includes(q) ||
        pm.name_en.toLowerCase().includes(q),
    );
  }, [paymentMethods, searchText]);

  const extractError = (error: unknown, fallback: string): string => {
    const detail = (
      error as { response?: { data?: { detail?: string | unknown[] } } }
    )?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    return (error as { message?: string })?.message || fallback;
  };

  const handleCreateSubmit = async (
    data: PaymentMethodCreatePayload | PaymentMethodUpdatePayload,
  ) => {
    try {
      await createMutation.mutateAsync(data as PaymentMethodCreatePayload);
      message.success(t("paymentMethodsConfig.createSuccess"));
      setCreateOpen(false);
    } catch (error: unknown) {
      message.error(
        extractError(error, t("paymentMethodsConfig.createError")),
      );
      throw error;
    }
  };

  const handleEditSubmit = async (
    data: PaymentMethodCreatePayload | PaymentMethodUpdatePayload,
  ) => {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, data });
      message.success(t("paymentMethodsConfig.editSuccess"));
      setEditTarget(null);
    } catch (error: unknown) {
      message.error(extractError(error, t("paymentMethodsConfig.editError")));
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      message.success(t("paymentMethodsConfig.deleteSuccess"));
      setDeleteTarget(null);
    } catch (error: unknown) {
      message.error(
        extractError(error, t("paymentMethodsConfig.deleteError")),
      );
      throw error;
    }
  };

  const totalCount = filtered.length;

  const lang = i18n.language?.startsWith("en") ? "en" : "bg";
  const primaryName = (pm: PaymentMethod) =>
    lang === "en" ? pm.name_en : pm.name_bg;

  const columns: ColumnsType<PaymentMethod> = [
    {
      title: t("paymentMethodsConfig.colName"),
      key: "name",
      sorter: (a, b) => primaryName(a).localeCompare(primaryName(b)),
      render: (_: unknown, record: PaymentMethod) => (
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: R.ink,
            letterSpacing: -0.1,
          }}
        >
          {primaryName(record)}
        </span>
      ),
    },
    {
      title: t("paymentMethodsConfig.colSystem"),
      key: "system",
      width: 160,
      render: (_: unknown, record: PaymentMethod) => {
        const sys = isSystemPaymentMethod(record.id);
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 10px",
              background: sys ? R.bg : accent.tint,
              color: sys ? R.textSoft : accent.deep,
              border: `1px solid ${sys ? R.border : accent.tintBorder}`,
              borderRadius: 6,
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: 0.2,
            }}
          >
            {sys && <LockOutlined style={{ fontSize: 11 }} />}
            {sys
              ? t("paymentMethodsConfig.badgeSystem")
              : t("paymentMethodsConfig.badgeCustom")}
          </span>
        );
      },
    },
    {
      title: t("paymentMethodsConfig.colSortIndex"),
      key: "sort_index",
      width: 130,
      align: "center",
      sorter: (a, b) => a.sort_index - b.sort_index,
      render: (_: unknown, record: PaymentMethod) => (
        <span
          style={{
            fontFamily:
              '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
            fontSize: 12.5,
            fontWeight: 500,
            color: R.textSoft,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: 0.3,
          }}
        >
          {record.sort_index}
        </span>
      ),
    },
    {
      title: t("paymentMethodsConfig.colActions"),
      key: "actions",
      width: 120,
      align: "right",
      render: (_: unknown, record: PaymentMethod) => {
        const sys = isSystemPaymentMethod(record.id);
        return (
          <div style={{ display: "inline-flex", gap: 6 }}>
            <PanelIconAction
              onClick={() => setEditTarget(record)}
              label={t("common.edit", "Edit")}
              accent={accent}
            >
              <EditOutlined />
            </PanelIconAction>
            {sys ? (
              <Tooltip title={t("paymentMethodsConfig.systemTooltip")}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 30,
                    width: 30,
                    borderRadius: 8,
                    border: `1px solid ${R.border}`,
                    color: R.textMuted,
                    cursor: "not-allowed",
                  }}
                >
                  <LockOutlined style={{ fontSize: 13 }} />
                </span>
              </Tooltip>
            ) : (
              <PanelIconAction
                onClick={() => setDeleteTarget(record)}
                label={t("common.delete", "Delete")}
                danger
                accent={accent}
              >
                <DeleteOutlined />
              </PanelIconAction>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <ConfigPanel
        scope="pm-list"
        accent={accent}
        icon={<CreditCardOutlined />}
        kicker={t("paymentMethodsConfig.listKicker")}
        title={t("paymentMethodsConfig.title")}
        count={totalCount}
        isNarrow={isNarrow}
        isEmpty={filtered.length === 0}
        emptyText={t("paymentMethodsConfig.noMethods")}
        toolbar={
          <>
            <Input
              placeholder={t("paymentMethodsConfig.searchPlaceholder")}
              prefix={<SearchOutlined style={{ color: R.textMuted }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: isNarrow ? "100%" : 260 }}
            />
            <PanelAddButton
              onClick={() => setCreateOpen(true)}
              label={t("paymentMethodsConfig.addMethod")}
              accent={accent}
              isNarrow={isNarrow}
            />
          </>
        }
      >
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          pagination={false}
          scroll={{ x: 700 }}
          size="small"
        />
      </ConfigPanel>

      <PaymentMethodModal
        open={createOpen}
        mode="create"
        onSubmit={handleCreateSubmit}
        onCancel={() => setCreateOpen(false)}
      />

      <PaymentMethodModal
        open={!!editTarget}
        mode="edit"
        paymentMethod={editTarget}
        onSubmit={handleEditSubmit}
        onCancel={() => setEditTarget(null)}
      />

      <DeleteConfirmModal
        open={!!deleteTarget}
        title={t("paymentMethodsConfig.deleteConfirmTitle")}
        description={t("paymentMethodsConfig.deleteConfirmContent", {
          name: deleteTarget ? primaryName(deleteTarget) : "",
        })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default PaymentMethodsTable;
